import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { all_routes } from '../../../router/all_routes';
import Table from '../../../../core/common/dataTable/index';
import { RootState } from '../../../../core/data/redux/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchTeacherAttendanceStart,
  fetchTeacherAttendanceSuccess,
  fetchTeacherAttendanceError,
} from '../../../../core/data/redux/teacherAtteandanceSlice';
import { fetchTeacherAttendanceApi } from '../../../../core/data/services/teacherAttendanceService';
import ErrorModal from '../../../../core/common/ErrorModal';
import SuccessModal from '../../../../core/common/successModal';


const TeacherAttendanceList: React.FC = () => {
  const routes = all_routes;
  const hasFetched = useRef(false);
  const dispatch = useDispatch();

  // Fetch user data to get schoolId
  const user = useSelector((state: RootState) => state.user.Users);
  const schoolId = user?.[0]?.relatedEntity?.schools?.id;

  // Fetch attendance data from Redux store
  const attendance = useSelector((state: RootState) => state.teacherAttendance.attendance);

  // Error and Success modal states
  const [error, setError] = useState<string>('');
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    // Set current date for attnDate
  const currentDate = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD

//   // Set previous date for attnDate using testing purpose because currently no data
//   const previousDate = new Date();
//   previousDate.setDate(previousDate.getDate() - 2); // Subtract 1 day
//   const formattedPreviousDate = previousDate.toISOString().split('T')[0]; // Format: 2025-07-22

  useEffect(() => {
    if (hasFetched.current || !schoolId) return;
    hasFetched.current = true;

    const fetchData = async () => {
      try {
        dispatch(fetchTeacherAttendanceStart(true));
        // Payload with schoolId and previous date
        const payload = {
          schoolId,
          attnDate: currentDate,
        };
        const teacherAttendance = await fetchTeacherAttendanceApi(payload);
        console.log('Fetched attendance data for previous date:', teacherAttendance);
        dispatch(fetchTeacherAttendanceSuccess({ data: teacherAttendance }));
      } catch (error: any) {
        const msg = error?.message || 'Failed to fetch attendance data.';
        dispatch(fetchTeacherAttendanceError(msg));
        setError(msg);
        setShowErrorModal(true);
        console.error('Error fetching data:', msg);
      }
    };

    if (schoolId) {
      fetchData();
    } else {
      const msg = 'School ID is not available. Please ensure user data is loaded.';
      dispatch(fetchTeacherAttendanceError(msg));
      setError(msg);
      setShowErrorModal(true);
    }
  }, [dispatch, schoolId,currentDate]);

  // Log attendance for debugging
  console.log('Attendance in Redux:', attendance);

  // Map attendance data to include teacherName
  const enhancedAttendance = attendance.map((att: any) => ({
    ...att,
    teacherName: `${att.firstName} ${att.lastName}` || 'Unknown Teacher',
    email: att.email || 'N/A',
  }));

  // Log enhanced attendance for debugging
  console.log('Enhanced attendance:', enhancedAttendance);

 const columns = [
    {
      title: 'Teacher Name',
      dataIndex: 'teacherName',
      render: (text: string) => text || 'Unknown Teacher',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      render: (text: string) => text || 'N/A',
    },
    {
      title: 'Clock In Time',
      dataIndex: 'clockInTime',
      render: (text: string) => {
        if (!text) return 'N/A';
        const date = new Date(text);
        return date.toLocaleTimeString('en-US', {
          timeZone: 'UTC',
          hour12: true // 24-hour format
        });
      },
    },
    {
      title: 'Clock Out Time',
      dataIndex: 'clockOutTime',
      render: (text: string) => {
        if (!text) return 'N/A';
        const date = new Date(text);
        return date.toLocaleTimeString('en-US', {
          timeZone: 'UTC',
          hour12: true // 24-hour format
        });
      },
    },
  ];

  // Modal handlers
  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setError('');
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setSuccessMessage('');
  };

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content content-two">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Teachers Attendance</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.HeadmasterDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Teachers Attendance
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Teachers Attendance List for {currentDate}</h4>
            </div>

            <div className="card-body p-0 py-3">
              {enhancedAttendance.length > 0 ? (
                <Table
                  dataSource={enhancedAttendance}
                  columns={columns}
                  Selection={true}
                  selectedRowKeys={selectedRowKeys}
                  setSelectedRowKeys={setSelectedRowKeys}
                />
              ) : (
                <div className="text-center p-3">
                  <p>No attendance data available for {currentDate}.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}

      {/* Error Modal */}
      <ErrorModal show={showErrorModal} message={error} onClose={handleCloseErrorModal} />

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        message={successMessage}
        onClose={handleCloseSuccessModal}
      />
    </>
  );
};

export default TeacherAttendanceList;